import React, { ChangeEvent, FC } from 'react'

import { TextField } from '@avantstay/backoffice-core'

import * as S from './RangeFilter.styles'

interface RangeFilterProps {
  fromValue?: string
  toValue?: string
  fromInputTypeIndicator?: string
  toInputTypeIndicator?: string
  label?: string
  minFromValue?: number
  maxToValue?: number
  onChangeFromValue: (event: ChangeEvent<HTMLInputElement>) => void
  onChangeToValue: (event: ChangeEvent<HTMLInputElement>) => void
  shouldDisplayFiltersOnSameLine?: boolean
  className?: string
  placeholder?: { from?: string; to?: string }
  isNegativeValueAllowed?: boolean
  invertRange?: boolean
  onChangeInvertRange?: (inverted: boolean) => void
}

export const RangeFilter: FC<RangeFilterProps> = ({
  fromValue,
  toValue,
  fromInputTypeIndicator,
  toInputTypeIndicator,
  label,
  minFromValue,
  maxToValue,
  onChangeFromValue,
  onChangeToValue,
  shouldDisplayFiltersOnSameLine,
  className,
  placeholder,
  isNegativeValueAllowed,
  invertRange,
  onChangeInvertRange,
}) => {
  const getLeftInputIndicator = (inputTypeIndicator?: string) =>
    inputTypeIndicator && <S.InputType>{inputTypeIndicator}</S.InputType>

  const negativeFromErrorMessage =
    fromValue !== '' && Number(fromValue) < 0 && !isNegativeValueAllowed
      ? 'Value cannot be negative'
      : undefined

  const maxFromErrorMessage = () => {
    if (fromValue !== '' && toValue !== '' && Number(fromValue) > Number(toValue)) {
      return `Value should not be greater than ${toValue}`
    }

    if (fromValue !== '' && maxToValue !== undefined && Number(fromValue) > maxToValue) {
      return `Value is out of range, max value is ${maxToValue}`
    }

    return undefined
  }

  const negativeToErrorMessage =
    toValue !== '' && Number(toValue) < 0 && !isNegativeValueAllowed
      ? 'To value cannot be negative'
      : undefined

  const maxToErrorMessage =
    toValue !== '' && maxToValue !== undefined && Number(toValue) > maxToValue
      ? `Value is out of range, max value is ${maxToValue}`
      : undefined

  const minFromErrorMessage =
    fromValue !== '' && minFromValue !== undefined && minFromValue > Number(fromValue)
      ? `Value is out of range, min value is ${minFromValue}`
      : undefined

  const handleInvertRangeChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChangeInvertRange?.(event.target.checked)
  }

  return (
    <div>
      {label && <S.FilterLabel>{label}</S.FilterLabel>}
      <S.Container
        shouldDisplayFiltersOnSameLine={shouldDisplayFiltersOnSameLine}
        className={className}
      >
        <TextField.Small
          value={fromValue || ''}
          placeholder={placeholder?.from ?? 'From'}
          onChange={onChangeFromValue}
          inputLeftIcon={getLeftInputIndicator(fromInputTypeIndicator)}
          type="number"
          error={negativeFromErrorMessage || minFromErrorMessage || maxFromErrorMessage()}
        />
        <TextField.Small
          value={toValue || ''}
          placeholder={placeholder?.to ?? 'To'}
          onChange={onChangeToValue}
          inputLeftIcon={getLeftInputIndicator(toInputTypeIndicator)}
          type="number"
          error={negativeToErrorMessage || maxToErrorMessage}
        />
      </S.Container>
      {onChangeInvertRange && (
        <S.CheckboxContainer>
          <S.CheckboxInput
            type="checkbox"
            checked={invertRange || false}
            onChange={handleInvertRangeChange}
          />
          <S.CheckboxLabel>Invert Range</S.CheckboxLabel>
        </S.CheckboxContainer>
      )}
    </div>
  )
}
